import { ArrowLeft, ShieldCheck, ShieldOff, UserCheck, UserX } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/status-badge";
import { adminApi, AdminDeployment, AdminProject, AdminUser } from "../../lib/adminApi";
import { notify } from "../../lib/notify";

function formatDate(d?: string) {
  return d ? new Date(d).toLocaleString() : "—";
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<{
    user: AdminUser;
    projects: AdminProject[];
    recentDeployments: AdminDeployment[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    adminApi.getUser(id).then(setData).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, [id]); // eslint-disable-line

  const update = async (body: { role?: "user" | "admin"; accountStatus?: "active" | "suspended" }, msg: string) => {
    if (!id) return;
    setActionLoading(true);
    try {
      await adminApi.updateUser(id, body);
      notify.success(msg);
      load();
    } catch (err: any) {
      notify.error(err?.message ?? "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (!data) return <p className="text-muted-foreground">User not found.</p>;

  const { user } = data;
  const projects = data.projects ?? [];
  const recentDeployments = data.recentDeployments ?? [];

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/users"><ArrowLeft className="mr-1 h-4 w-4" />Users</Link>
        </Button>
      </div>

      {/* User info card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">User</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Email</p>
              <p className="text-sm font-medium">{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Name</p>
              <p className="text-sm font-medium">{user.name || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Joined</p>
              <p className="text-sm">{formatDate(user.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Role</p>
              {user.role === "admin" ? (
                <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">Admin</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">User</Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Status</p>
              <Badge variant="outline" className={user.accountStatus === "active" ? "text-success border-success/30 text-xs" : "text-muted-foreground text-xs"}>
                {user.accountStatus ?? "active"}
              </Badge>
            </div>
          </div>
          <Separator />
          <div className="flex flex-wrap gap-2">
            {user.accountStatus === "suspended" ? (
              <Button size="sm" variant="outline" disabled={actionLoading}
                onClick={() => update({ accountStatus: "active" }, "User activated")}>
                <UserCheck className="mr-1.5 h-3.5 w-3.5" />Activate
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={actionLoading}
                onClick={() => update({ accountStatus: "suspended" }, "User suspended")}>
                <UserX className="mr-1.5 h-3.5 w-3.5" />Suspend
              </Button>
            )}
            {user.role === "admin" ? (
              <Button size="sm" variant="outline" disabled={actionLoading}
                onClick={() => update({ role: "user" }, "Admin demoted")}>
                <ShieldOff className="mr-1.5 h-3.5 w-3.5" />Demote to User
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={actionLoading}
                onClick={() => update({ role: "admin" }, "User promoted to admin")}>
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />Promote to Admin
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Projects */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Projects ({projects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <p className="text-sm text-muted-foreground">No projects.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Name</th>
                  <th className="pb-2 text-left font-medium">Role</th>
                  <th className="pb-2 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {projects.map((p) => (
                  <tr key={p._id ?? p.id}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => { const id = p._id ?? p.id; if (id) window.open(`/projects/${id}`, "_blank"); }}>
                    <td className="py-2 pr-4 font-medium">{p.name}</td>
                    <td className="py-2 pr-4">
                      {p._memberRole
                        ? <Badge variant="outline" className="text-xs capitalize">{p._memberRole}</Badge>
                        : <Badge variant="outline" className="text-xs">Owner</Badge>}
                    </td>
                    <td className="py-2 text-muted-foreground">{formatDate(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Recent deployments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent Deployments ({recentDeployments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {recentDeployments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No deployments.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Service / Project</th>
                  <th className="pb-2 text-left font-medium">Status</th>
                  <th className="pb-2 text-left font-medium">Triggered by</th>
                  <th className="pb-2 text-left font-medium">Commit</th>
                  <th className="pb-2 text-left font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentDeployments.map((d) => {
                  const depId = d._id ?? d.id;
                  return (
                    <tr key={depId}
                      className="cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => { if (d.projectId && depId) window.open(`/projects/${d.projectId}/deployments/${depId}`, "_blank"); }}>
                      <td className="py-2 pr-4">
                        <p className="font-medium">{d.serviceName ?? d.serviceId ?? "—"}</p>
                        <p className="text-xs text-muted-foreground">{d.projectName ?? d.projectId ?? ""}</p>
                      </td>
                      <td className="py-2 pr-4">
                        <StatusBadge kind="deployment" status={d.status ?? "unknown"} />
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {d.triggeredByEmail
                          ? <span title={d.triggeredBy}>{d.triggeredByEmail}</span>
                          : d.trigger === "git-push"
                            ? <Badge variant="outline" className="text-xs">git-push</Badge>
                            : "—"}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                        {d.commitHash ? d.commitHash.slice(0, 7) : (d.gitCommit ? d.gitCommit.slice(0, 7) : "—")}
                      </td>
                      <td className="py-2 text-muted-foreground text-xs">{formatDate(d.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
